/**
 * Pasek akcji masowej kokpitu decyzji: „Akceptuj zaznaczone" + „Wyczyść" (te same `data-testid` co
 * zawsze: `bulk-action-bar`, `bulk-approve-btn`, `bulk-clear-btn`).
 *
 *  - `DecisionBulkActions` — same przyciski; zwykła lista wkłada je w pasek zaznaczenia SAMEJ tabeli
 *    (`ConfigurableTable.renderBulkActions`, tabela dopisuje licznik).
 *  - `DecisionSectionsBulkBar` — JEDEN pasek w trybie sekcji kartotek: licznik + te same przyciski.
 *    Zaznaczenie jest tam rozrzucone po kilku tabelach (sekcje i tabela pozostałych kartotek), więc
 *    żadna z nich nie ma własnego paska — liczy się wspólny, nad sekcjami.
 *  - `DecisionSectionsSelectionStatus` — STAŁY region `role="status"` (wizualnie ukryty) w pasku trybu
 *    sekcji, do którego licznik trafia przy zaznaczeniu: pusty region obecny od początku jest
 *    zapowiadany przez czytnik ekranu przy zmianie treści, element dołożony razem z treścią (sam pasek
 *    pojawia się dopiero przy zaznaczeniu) często nie. Widoczny licznik na pasku nie ma więc własnej roli.
 * Licznik liczy DECYZJE (kroki czekające), nie rekordy: rekord w sekcji bywa na kilku krokach naraz.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';

export interface DecisionBulkActionsProps {
  onApprove: () => void;
  onClear: () => void;
}

export function DecisionBulkActions({
  onApprove,
  onClear,
}: DecisionBulkActionsProps): ReactElement {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="bulk-action-bar">
      <Button variant="primary" size="sm" onClick={onApprove} data-testid="bulk-approve-btn">
        {t('tenant.processes.approvals.bulk.approveSelected')}
      </Button>
      <Button variant="ghost" size="sm" onClick={onClear} data-testid="bulk-clear-btn">
        {t('tenant.processes.approvals.bulk.clear')}
      </Button>
    </div>
  );
}

export interface DecisionSectionsBulkBarProps extends DecisionBulkActionsProps {
  /** Liczba zaznaczonych decyzji (kroków) na całym ekranie. */
  count: number;
}

export function DecisionSectionsBulkBar({
  count,
  onApprove,
  onClear,
}: DecisionSectionsBulkBarProps): ReactElement {
  const { t } = useTranslation();
  return (
    <div
      className="flex flex-wrap items-center gap-4 rounded-xl border border-accent-200 bg-accent-50 px-4 py-2"
      data-testid="approvals-sections-bulk-bar"
    >
      <span className="text-sm font-medium text-accent-800">
        {t('ui.configurableTable.selectedCount', { count })}
      </span>
      <DecisionBulkActions onApprove={onApprove} onClear={onClear} />
    </div>
  );
}

export interface DecisionSectionsSelectionStatusProps {
  /** Liczba zaznaczonych decyzji (kroków) na całym ekranie; `0` = region pusty. */
  count: number;
}

export function DecisionSectionsSelectionStatus({
  count,
}: DecisionSectionsSelectionStatusProps): ReactElement {
  const { t } = useTranslation();
  return (
    <div role="status" className="sr-only" data-testid="approvals-sections-selection-status">
      {count > 0 ? t('ui.configurableTable.selectedCount', { count }) : null}
    </div>
  );
}
